// Pages/Vendors/VendorSignupPage.jsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Paper,
  Stack,
  LinearProgress,
  Alert,
  Card,
  CardContent,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  CheckCircle as CheckCircleIcon,
  Save as SaveIcon,
  Info as InfoIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Star as StarIcon,
  Speed as SpeedIcon,
  PaymentRounded as PaymentIcon,
  Groups as GroupsIcon,
  Analytics as AnalyticsIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { useMutation } from "react-query";
import client from "./../../../API";
import { parseLocalFloat, parseLocalInt } from "../../../Utils/constants";
import MuiPhoneInput from "../../../Components/MuiPhoneInput";

const VendorSignupPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Success state
  const [showSuccess, setShowSuccess] = useState(false);
  const [vendorId, setVendorId] = useState(null);
  const [countdown, setCountdown] = useState(5);

  // Form State
  const [formData, setFormData] = useState({
    creatorName: "",
    contactEmail: "",
    paymentEmail: "",
    phoneNumber: "",
    emailSame: false,
    creatorRegion: "",
    notesContentStyle: "",
    
    // Social Media
    instagramLink: "",
    instagramBrandRate: "",
    instagramFollowerCount: "",
    instagramSongRate: "",
    
    tikTokLink: "",
    tikTokFollowerCount: "",
    tikTokBrandRate: "",
    tikTokSongRate: "",
    
    youtubeLink: "",
    youtubeFollowerCount: "",
    youtubeShortsBrandRate: "",
  });

  // Form validation
  const isFormValid = useMemo(() => {
    return !!(
      formData.creatorName &&
      formData.creatorRegion &&
      formData.contactEmail &&
      formData.paymentEmail &&
      formData.phoneNumber &&
      formData.phoneNumber.length >= 11
    );
  }, [formData]);

  // Completion percentage
  const completionPercentage = useMemo(() => {
    const requiredFields = ['creatorName', 'creatorRegion', 'contactEmail', 'paymentEmail'];
    const filledRequired = requiredFields.filter(field => formData[field]).length;
    const phoneValid = formData.phoneNumber.length >= 11 ? 1 : 0;
    
    const optionalFields = ['notesContentStyle', 'instagramFollowerCount', 'tikTokFollowerCount', 'youtubeFollowerCount'];
    const filledOptional = optionalFields.filter(field => formData[field]).length;
    
    const basePercentage = ((filledRequired + phoneValid) / 5) * 70;
    const bonusPercentage = (filledOptional / optionalFields.length) * 30;
    
    return Math.round(basePercentage + bonusPercentage);
  }, [formData]);

  const handleInputChange = useCallback((field) => (e) => {
    const value = e.target?.value !== undefined ? e.target.value : e;
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleEmailSameChange = useCallback((event) => {
    const checked = event.target.checked;
    setFormData(prev => ({
      ...prev,
      emailSame: checked,
      paymentEmail: checked ? prev.contactEmail : ""
    }));
  }, []);

  const { mutate: submitVendorSignup, isLoading } = useMutation(
    client.creators.vendorSignup,
    {
      onSuccess: (data) => {
        const creatorId = data?.vendor_id || formData.creatorName;
        setVendorId(creatorId);
        setShowSuccess(true);
        setCountdown(5); // Reset countdown
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || "Registration failed. Please try again.";
        alert(errorMsg);
      },
    }
  );

  // Auto-navigate countdown
  useEffect(() => {
    if (!showSuccess || countdown <= 0) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Navigate when countdown reaches 0
          navigate(`/creatorconnect/${vendorId}`);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [showSuccess, countdown, navigate, vendorId]);

  const handleClaimNow = () => {
    navigate(`/creatorconnect/${vendorId}`);
  };

  const handleSkip = () => {
    navigate('/');
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!isFormValid) {
      alert("Please complete all required fields");
      return;
    }

    if (formData.phoneNumber.length < 11) {
      alert("Please enter a valid phone number");
      return;
    }

    const payload = {
      creator: formData.creatorName,
      contactEmail: formData.contactEmail,
      paymentEmail: formData.paymentEmail,
      phone_number: formData.phoneNumber,
      region: formData.creatorRegion,
      notescontent_style: formData.notesContentStyle || null,
      
      // Social media
      tiktok: parseLocalInt(formData.tikTokFollowerCount) || null,
      tiktok_link: formData.tikTokLink ? `https://www.tiktok.com/@${formData.tikTokLink}` : null,
      instagram: parseLocalInt(formData.instagramFollowerCount) || null,
      instagram_link: formData.instagramLink ? `https://www.instagram.com/${formData.instagramLink}` : null,
      youtube: parseLocalInt(formData.youtubeFollowerCount) || null,
      youtube_link: formData.youtubeLink ? `https://www.youtube.com/${formData.youtubeLink}` : null,
      
      // Rates
      rate_tt_sound: parseLocalFloat(formData.tikTokSongRate),
      rate_tt_brand: parseLocalFloat(formData.tikTokBrandRate),
      rate_ig_reelsound: parseLocalFloat(formData.instagramSongRate),
      rate_ig_reelbrand: parseLocalFloat(formData.instagramBrandRate),
      rate_yt_short: parseLocalFloat(formData.youtubeShortsBrandRate),
    };

    submitVendorSignup(payload);
  }, [formData, isFormValid, submitVendorSignup]);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: isMobile ? 2 : 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 2 : 4,
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            borderRadius: 2,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <BusinessIcon sx={{ fontSize: 48 }} />
            <Box>
              <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700}>
                Vendor Registration
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Join our network of trusted service providers
              </Typography>
            </Box>
          </Stack>
          
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="caption">Profile Completion</Typography>
              <Typography variant="caption" fontWeight={600}>
                {completionPercentage}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'white',
                  borderRadius: 3,
                }
              }}
            />
          </Box>
        </Paper>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Main Form */}
            <Grid item xs={12} md={8}>
              {/* Basic Information */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Vendor Name"
                      fullWidth
                      required
                      value={formData.creatorName}
                      onChange={handleInputChange("creatorName")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required>
                      <InputLabel>Region</InputLabel>
                      <Select
                        label="Region"
                        value={formData.creatorRegion}
                        onChange={(e) => handleInputChange("creatorRegion")(e)}
                      >
                        <MenuItem value="NACAUKAUS">USA/Canada/UK/Australia</MenuItem>
                        <MenuItem value="Europe">Europe</MenuItem>
                        <MenuItem value="Asia">Asia</MenuItem>
                        <MenuItem value="LATAM">LATAM</MenuItem>
                        <MenuItem value="Africa">Africa</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Bio"
                      fullWidth
                      multiline
                      rows={4}
                      placeholder="Tell us about your services and expertise..."
                      value={formData.notesContentStyle}
                      onChange={handleInputChange("notesContentStyle")}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Contact Information */}
              <Paper elevation={0} sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Contact Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Contact Email"
                      type="email"
                      fullWidth
                      required
                      value={formData.contactEmail}
                      onChange={handleInputChange("contactEmail")}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Payment Email"
                      type="email"
                      fullWidth
                      required
                      disabled={formData.emailSame}
                      value={formData.paymentEmail}
                      onChange={handleInputChange("paymentEmail")}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <MuiPhoneInput
                      country={'us'}
                      preferredCountries={['us']}
                      value={formData.phoneNumber}
                      onChange={handleInputChange("phoneNumber")}
                      isValid={(value) => value.length >= 11}
                      required
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.emailSame}
                          onChange={handleEmailSameChange}
                        />
                      }
                      label="Payment and contact email are the same"
                    />
                  </Grid>
                </Grid>
              </Paper>              
            </Grid>

            {/* Sidebar - Preview/Status */}
            <Grid item xs={12} md={4}>
              <Card sx={{ position: 'sticky', top: 16 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    Application Status
                  </Typography>
                  
                  {isFormValid ? (
                    <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                      Ready to submit!
                    </Alert>
                  ) : (
                    <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
                      Complete required fields
                    </Alert>
                  )}

                  <Stack spacing={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      fullWidth
                      disabled={!isFormValid || isLoading}
                      startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {isLoading ? 'Submitting...' : 'Submit Application'}
                    </Button>
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate('/')}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </form>

        {/* Success Dialog */}
        <Dialog
          open={showSuccess}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              p: 2,
            }
          }}
        >
          <DialogContent>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <CheckCircleIcon 
                sx={{ 
                  fontSize: 80, 
                  color: 'success.main',
                  mb: 2,
                }} 
              />
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Registration Successful! 🎉
              </Typography>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Your vendor profile has been created.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" fontWeight={600} gutterBottom>
              Claim Your Full Blitz Account
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Get access to powerful creator tools and features:
            </Typography>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <PaymentIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Fast Payments" 
                  secondary="Get paid instantly with BlitzPay - no more waiting 30-90 days"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <SpeedIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Campaign Management" 
                  secondary="Track all your brand deals and collaborations in one place"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <GroupsIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Brand Connections" 
                  secondary="Get discovered by top brands looking for creators like you"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <AnalyticsIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Performance Analytics" 
                  secondary="Track your earnings, campaigns, and growth metrics"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <VerifiedIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Professional Profile" 
                  secondary="Showcase your work with a customizable media kit"
                />
              </ListItem>
            </List>

            <Alert 
              severity="info" 
              icon={<InfoIcon />} 
              sx={{ mt: 3 }}
            >
              Redirecting to account setup in <strong>{countdown}</strong> second{countdown !== 1 ? 's' : ''}...
            </Alert>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button 
              onClick={handleSkip} 
              variant="outlined"
              sx={{ minWidth: 100 }}
            >
              Skip for Now
            </Button>
            <Button 
              onClick={handleClaimNow} 
              variant="contained"
              size="large"
              sx={{ minWidth: 150 }}
            >
              Claim Account Now
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default VendorSignupPage;